import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { SubmitFeedbackRequest_Rating } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-quality/v1/api.pb';
import { FeedbackType, Rating } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { NetworkPerformance } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import { CallIntelMutations, CallIntelTypes } from '@frontend/api-call-intel';
import { CallQualityV1 } from '@frontend/api-call-quality';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { CallInsightFeedbackCallout } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PriorityRendererItemWrapper } from '@frontend/priority-renderer';
import { breakpoints } from '@frontend/responsiveness';
import { sentry } from '@frontend/tracking';
import { ComponentProps } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, Text, useModalControl, useAlert, useTooltip } from '@frontend/design-system';
import { CIBannerEnum, CIBannerPriorityGroupEnum, FeedbackFeature, FeedbackFeatureType } from '../constants';
import { generateTrackingId } from '../utils/tracking';
import { FeedbackModalV2 } from './modal/feedback-modal-v2';

type Props = {
  call: HydratedCallRecord;
  callMetadata?: CallIntelTypes.CallMetadata;
  refetchCallMetadata?: () => void;
  isDemoAccount?: boolean;
  showCallIntelligenceOption?: boolean;
  showNetworkPerformanceOption?: boolean;
  showAIROption?: boolean;
  networkPerformance?: {
    hasPerformanceIssue: boolean;
    performanceStatus?: NetworkPerformance;
  };
  containerStyles?: Interpolation<Theme>;
};

export const CallIntelFeedbackBannerV2: FC<Props> = ({
  call,
  callMetadata,
  containerStyles,
  refetchCallMetadata,
  showCallIntelligenceOption,
  showNetworkPerformanceOption,
  showAIROption,
  networkPerformance,
  isDemoAccount,
}) => {
  const { t } = useTranslation('call-intelligence');
  const alert = useAlert();
  const { modalProps, openModal, closeModal } = useModalControl();
  const [isPositiveFeedback, setIsPositiveFeedback] = useState(false);
  const [existingRating, setExistingRating] = useState<keyof typeof Rating | undefined>(callMetadata?.rating);

  const { Tooltip, tooltipProps, triggerProps: tooltipTriggerProps } = useTooltip();

  const bannerRef = useRef<HTMLDivElement>(null);

  const demoHttpOptions = isDemoAccount ? { headers: { 'X-Is-Demo': 'true' } } : undefined;

  const { mutateAsync: submitIntelFeedback, isLoading: isIntelFeedbackMutating } = CallIntelMutations.useSubmitFeedback(
    { httpOptions: demoHttpOptions }
  );

  const { mutateAsync: submitQualityFeedback, isLoading: isQualityFeedbackMutating } =
    CallQualityV1.Mutations.useSubmitFeedbackMutation({ httpOptions: demoHttpOptions });

  const isWeaveUserAndProd = isWeaveUser() && appConfig.BACKEND_API.includes('api.weaveconnect.com');

  const availableFeatures = useMemo(() => {
    return [
      ...(showCallIntelligenceOption ? [FeedbackFeature.FEATURE_CALL_INTELLIGENCE] : []),
      ...(showNetworkPerformanceOption ? [FeedbackFeature.FEATURE_NETWORK_PERFORMANCE] : []),
      ...(showAIROption ? [FeedbackFeature.FEATURE_AI_RECEPTIONIST] : []),
      FeedbackFeature.FEATURE_OTHER,
    ];
  }, [showCallIntelligenceOption, showNetworkPerformanceOption, showAIROption]);

  const handleAction = async (positive: boolean) => {
    if (isWeaveUserAndProd) {
      alert.warning(
        t(
          'This feature is intended solely for Weave customers to provide feedback on AI outputs perceived as wrongly classified. If you are not a Weave customer, we advise you to skip this module.'
        )
      );
      return;
    }

    const userID = getUser()?.userID;

    const callId = call.channelId;

    if (!callId || !call?.locationId || !userID) {
      alert.error(t('Unable to submit feedback. Missing call or user information.'));
      return;
    }

    if (positive) {
      try {
        await submitIntelFeedback({
          callId: callId,
          rating: Rating.RATING_POSITIVE,
          userId: userID,
          locationId: call.locationId,
          feedbackType: FeedbackType.FEEDBACK_AI,
          comment: '',
        });
        alert.success(t('Rating submitted. Thank you!'));
        setExistingRating('RATING_POSITIVE');
        if (!!call.callIntelInsights) {
          refetchCallMetadata?.();
        }
      } catch (error) {
        alert.error(t('Failed to submit rating. Please try again.'));
        sentry.warn({
          team: 'analytics',
          topic: 'analytics',
          error: `Failed to submit call insights feedback rating: ${error}`,
        });
        return;
      }
    }
    setIsPositiveFeedback(positive);
    openModal();
  };

  const handleFeedbackModalSubmit: ComponentProps<typeof FeedbackModalV2>['onSubmit'] = async ({
    isPositive,
    feedbackFeatures,
    featureFeedbackGroupList,
    feedback: message,
  }) => {
    const userID = getUser()?.userID;

    const promises: Promise<unknown>[] = [];

    const callId = call.channelId;

    const callInsightFeatureFeedbackList = featureFeedbackGroupList?.filter(
      ({ feature }) => (feature as FeedbackFeatureType) !== FeedbackFeature.FEATURE_NETWORK_PERFORMANCE
    );

    if (callId && userID && call?.locationId && (isPositive || callInsightFeatureFeedbackList?.length)) {
      submitIntelFeedback({
        callId,
        rating: isPositive ? Rating.RATING_POSITIVE : Rating.RATING_NEGATIVE,
        userId: userID,
        locationId: call.locationId,
        feedbackType: FeedbackType.FEEDBACK_AI,
        comment: message,
        featureFeedbacks: callInsightFeatureFeedbackList,
      });

      if (!!call.callIntelInsights) {
        refetchCallMetadata?.();
      }
    }

    if (
      showNetworkPerformanceOption &&
      call?.startedAt &&
      (isPositive || feedbackFeatures.includes(FeedbackFeature.FEATURE_NETWORK_PERFORMANCE))
    ) {
      submitQualityFeedback({
        callStartedAt: call.startedAt,
        channelId: call.channelId ?? '',
        locationId: call.locationId ?? '',
        rating: isPositive
          ? SubmitFeedbackRequest_Rating.RATING_POSITIVE
          : SubmitFeedbackRequest_Rating.RATING_NEGATIVE,
        feedbackText: message,
        networkPerformance:
          networkPerformance?.performanceStatus ??
          (isPositive
            ? NetworkPerformance.NETWORK_PERFORMANCE_GOOD
            : NetworkPerformance.NETWORK_PERFORMANCE_ISSUE_DETECTED),
      });
    }

    try {
      await Promise.all(promises);
      alert.success({ message: t('Feedback submitted successfully. Thank you!', { ns: 'call-intelligence' }) });
      setExistingRating(isPositive ? 'RATING_POSITIVE' : 'RATING_NEGATIVE');
      closeModal();
    } catch (error) {
      alert.error({ message: t('Failed to submit feedback. Please try again.', { ns: 'call-intelligence' }) });
      sentry.warn({
        team: 'analytics',
        topic: 'analytics',
        error: `Failed to submit call insights feedback: ${error}`,
      });
    }
  };

  useEffect(() => {
    if (callMetadata?.rating) {
      setExistingRating(callMetadata.rating);
    }
  }, [callMetadata?.rating]);

  const isDisabled = isIntelFeedbackMutating || isQualityFeedbackMutating || isWeaveUserAndProd;

  const feedbackContainerRef = composeRefs(bannerRef, isWeaveUserAndProd ? tooltipTriggerProps.ref : null);

  return (
    <div css={[styles.container, containerStyles]} data-testid='call-intel-feedback-banner'>
      <PriorityRendererItemWrapper<typeof CallInsightFeedbackCallout>
        itemName={CIBannerEnum.CALL_INSIGHT_GLOBAL_FEEDBACK}
        groupName={CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL}
        closeFnProp={'onDismiss'}
      >
        <CallInsightFeedbackCallout
          locationId={call.locationId}
          isAIRCall={showAIROption}
          isDemo={isDemoAccount}
          anchorRef={bannerRef}
        />
      </PriorityRendererItemWrapper>
      <div css={styles.banner} ref={feedbackContainerRef as React.RefObject<HTMLDivElement>}>
        <Icon name='user-voice' css={styles.feedbackIcon} />
        <Text size='medium' css={styles.bannerText}>
          {t('How was this call?')}
        </Text>
        <div css={styles.actions}>
          <Button
            iconName='feedback'
            variant='secondary'
            size='small'
            css={[styles.iconButton, existingRating === 'RATING_POSITIVE' && styles.positiveActive]}
            onClick={() => handleAction(true)}
            disabled={isDisabled}
            hoverLabel={t('Share positive feedback')}
            aria-label={t('Share positive feedback')}
            trackingId={generateTrackingId({ customName: 'positive-call-feedback' })}
          />
          <Button
            iconName='feedback-bad'
            variant='secondary'
            size='small'
            css={[styles.iconButton, existingRating === 'RATING_NEGATIVE' && styles.negativeActive]}
            onClick={() => handleAction(false)}
            hoverLabel={t('Report an issue')}
            aria-label={t('Report an issue')}
            trackingId={generateTrackingId({ customName: 'negative-call-feedback' })}
            disabled={isDisabled}
          />
        </div>
      </div>

      {isWeaveUserAndProd && (
        <Tooltip {...tooltipProps}>
          {t(
            'This feature is intended solely for Weave customers to provide feedback on AI outputs perceived as wrongly classified. If you are not a Weave customer, we advise you to skip this module.'
          )}
        </Tooltip>
      )}
      <FeedbackModalV2
        modalProps={modalProps}
        isPositiveFeedback={isPositiveFeedback}
        onSubmit={handleFeedbackModalSubmit}
        isLoading={isIntelFeedbackMutating || isQualityFeedbackMutating}
        availableFeatures={availableFeatures}
        trackingIdBase={generateTrackingId({ customName: 'call-insight-feedback-v2' })}
      />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    align-items: center;
  `,
  banner: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.full};
  `,
  bannerText: css`
    @media (max-width: ${breakpoints.xsmall.max}px) {
      display: none;
    }
  `,
  feedbackIcon: css`
    color: ${theme.colorsV2.blue70()};
    background-color: ${theme.colorsV2.blue50(10)};
    border-radius: ${theme.borderRadius.full};
    padding: ${theme.spacing(0.5)};
  `,
  actions: css`
    display: flex;
    gap: ${theme.spacing(0.5)};
  `,
  iconButton: css`
    padding: ${theme.spacing(0.5)};
    min-width: unset;
    width: 24px;
    height: 24px;
  `,
  positiveActive: css`
    background-color: ${theme.colors.success5};
  `,
  negativeActive: css`
    background-color: ${theme.colors.critical5};
  `,
};
